import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { flattenKubernetesTargets } from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import { ServiceTypeCollectionMessage } from 'components/services/create/ServiceTypeCollectionMessage'
import {
  getKubernetesTargetingStrategy,
  getTargetingStrategy
} from 'components/services/create/target-selection/service-target-utils'
import { MAX_SERVICE_NAME_LENGTH } from 'components/services/service-utils'
import {
  activeInfraClientSelector,
  activeK8sClientSelector,
  useInfraClientsStore,
  useK8sClientsStore
} from 'hooks/use-clients'
import { ServiceCreationReadiness } from 'types/api/services/service-creation-readiness'
import { ServiceFormValues } from 'types/forms'

export const ServiceType: React.FC<{
  serviceCreationReadiness: ServiceCreationReadiness
  setDisableFutureSteps(boolean): void
}> = ({ serviceCreationReadiness, setDisableFutureSteps }) => {
  const { control, formState, register, getValues, setValue } =
    useFormContext<ServiceFormValues>()

  const targetType = useWatch<ServiceFormValues>({ name: 'type' })

  const activeClients = useInfraClientsStore(activeInfraClientSelector)
  const k8sClientsRaw = useK8sClientsStore(activeK8sClientSelector)
  const k8sClientsFlat = flattenKubernetesTargets(k8sClientsRaw)

  const numClientsWithZoneTag = Object.values(activeClients).filter((c) =>
    Object.keys(c.tags).includes('zone')
  ).length

  const allHostsHaveZoneTags = numClientsWithZoneTag === activeClients.length

  const handleSelectServiceType = (e, field) => {
    const newTargetType: ProvenanceTargetType = e.target.value

    setValue('targetParams', {})
    setValue('selectedClients', [])
    setValue('selectedObjects', new Set())
    if (
      newTargetType === ProvenanceTargetType.ProcessData ||
      newTargetType === ProvenanceTargetType.Container
    ) {
      setValue('targetingStrategy', getTargetingStrategy(newTargetType, {}))
    } else {
      setValue(
        'targetingStrategy',
        getKubernetesTargetingStrategy(k8sClientsFlat, getValues())
      )
    }

    setDisableFutureSteps(true)
    field.onChange(newTargetType)
  }

  return (
    <>
      <Typography data-test-service-type-description variant="body1">
        The first step toward getting a Reliability Score is defining your
        Service. Give your Service a name and specify whether it is a container,
        host, or Kubernetes service.
      </Typography>
      <FormControl fullWidth>
        <TextField
          autoFocus
          data-test-service-type-target-selection-name
          error={Boolean(formState.errors.name)}
          helperText={formState.errors.name?.message}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: MAX_SERVICE_NAME_LENGTH }}
          label="Name"
          placeholder="Service Name"
          variant="outlined"
          {...register('name')}
        />
      </FormControl>
      <FormControl fullWidth>
        <InputLabel id="service-type">Type of Service</InputLabel>
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Select
              {...field}
              data-test-service-type-menu
              label="Type of Service"
              labelId="service-type"
              onChange={(e) => handleSelectServiceType(e, field)}
            >
              <MenuItem data-test-service-type="Host" value="Host">
                Host
              </MenuItem>
              <MenuItem data-test-service-type="Container" value="Container">
                Container
              </MenuItem>
              <MenuItem data-test-service-type="Kubernetes" value="Kubernetes">
                Kubernetes
              </MenuItem>
            </Select>
          )}
        />
        <Stack spacing={2} sx={{ marginTop: 2 }}>
          <ServiceTypeCollectionMessage
            serviceCreationReadiness={serviceCreationReadiness}
            targetType={targetType}
          />
          {!allHostsHaveZoneTags && (
            <Alert data-test-zone-tag-alert severity="info">
              Not all agents have a zone tag defined
            </Alert>
          )}
        </Stack>
      </FormControl>
    </>
  )
}
